export interface SampleEvent {
  id: number
  title: string
  date: string
  time: string
  location: string
  type: string
  image: string
  description: string
  attendees: number
  maxAttendees: number
  ticketPrice?: number
  price?: string
  isFree?: boolean
  includesBook?: boolean
  isVirtual?: boolean
  status?: { label: string; classes: string }
  country: string
  author?: string
  link?: string
}

export interface EventCategoryConfig {
  slug: string
  title: string
  subtitle: string
  searchPlaceholder: string
  attendeeLabel: string
  emptyMessage: string
  sampleEvents: SampleEvent[]
}

export const EVENT_CATEGORIES: Record<string, EventCategoryConfig> = {
  'book-signings': {
    slug: 'book-signings',
    title: 'Book Signings',
    subtitle: 'Meet your favorite authors and get your books signed',
    searchPlaceholder: 'Search book signings by location...',
    attendeeLabel: 'attending',
    emptyMessage: 'No book signings found in this location.',
    sampleEvents: [
      {
        id: 1,
        title: 'Book Signing: The Silent Echo',
        author: 'Elena Richards',
        date: 'Oct 15, 2026',
        time: '2:00 PM - 4:00 PM',
        location: 'Bright Pages Bookstore, Seattle',
        type: 'Book Signing',
        image:
          'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=800',
        description:
          'Meet bestselling author Elena Richards as she signs copies of her latest thriller.',
        attendees: 45,
        maxAttendees: 100,
        ticketPrice: 25,
        includesBook: true,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
      {
        id: 2,
        title: 'Meet & Greet with Marcus Chen',
        author: 'Marcus Chen',
        date: 'Oct 25, 2026',
        time: '3:00 PM - 5:00 PM',
        location: 'Literary Corner Bookshop, Portland',
        type: 'Book Signing',
        image:
          'https://images.unsplash.com/photo-1517971129774-8a2b38fa128e?w=800',
        description:
          'Marcus Chen will be signing copies of his bestselling fantasy series and taking photos with fans.',
        attendees: 32,
        maxAttendees: 75,
        ticketPrice: 30,
        includesBook: true,
        status: { label: 'Sold Out', classes: 'bg-red-100 text-red-800' },
        country: 'USA',
      },
      {
        id: 3,
        title: 'Book Launch: Midnight Whispers',
        author: 'Sarah Johnson',
        date: 'Nov 5, 2026',
        time: '6:00 PM - 8:00 PM',
        location: 'Chapters Bookstore, Chicago',
        type: 'Book Signing',
        image:
          'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800',
        description:
          "Join us for the official launch of Sarah Johnson's new horror novel with readings and signings.",
        attendees: 58,
        maxAttendees: 120,
        isFree: true,
        price: 'Free',
        country: 'USA',
      },
    ],
  },
  'book-launches': {
    slug: 'book-launches',
    title: 'Book Launch Events',
    subtitle:
      'Celebrate new releases with launch parties and first-look events',
    searchPlaceholder: 'Search book launches by location...',
    attendeeLabel: 'attending',
    emptyMessage: 'No book launch events found.',
    sampleEvents: [
      {
        id: 101,
        title: 'Midnight Whispers Launch Party',
        date: 'Nov 5, 2026',
        time: '7:00 PM - 10:00 PM',
        location: 'The Strand Bookstore, New York',
        type: 'Book Launch',
        image:
          'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800',
        description:
          'Celebrate the release of the most anticipated thriller of the year with readings, signings, and cocktails.',
        attendees: 85,
        maxAttendees: 150,
        ticketPrice: 15,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
      {
        id: 102,
        title: 'Virtual Launch: Crown of Stars',
        date: 'Nov 20, 2026',
        time: '8:00 PM - 9:30 PM EST',
        location: 'Zoom',
        type: 'Book Launch',
        image:
          'https://images.unsplash.com/photo-1576872381149-7847515ce5d8?w=800',
        description:
          'Join the virtual launch of the epic fantasy finale. Includes a live reading, Q&A, and exclusive cover reveal.',
        attendees: 200,
        maxAttendees: 500,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
      {
        id: 103,
        title: 'Romance Release Celebration',
        date: 'Dec 1, 2026',
        time: '6:00 PM - 9:00 PM',
        location: 'Book Passage, San Francisco',
        type: 'Book Launch',
        image:
          'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800',
        description:
          'A glamorous evening celebrating the release of three new romance novels with wine, readings, and signed copies.',
        attendees: 60,
        maxAttendees: 80,
        ticketPrice: 20,
        includesBook: true,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
    ],
  },
  'author-talks': {
    slug: 'author-talks',
    title: 'Author Talks & Readings',
    subtitle:
      'Hear authors discuss their craft, read from their work, and share insights',
    searchPlaceholder: 'Search author talks by location...',
    attendeeLabel: 'attending',
    emptyMessage: 'No author talks found.',
    sampleEvents: [
      {
        id: 201,
        title: 'Elena Richards: Writing Dark Romance',
        date: 'Oct 20, 2026',
        time: '7:00 PM - 9:00 PM',
        location: 'Elliott Bay Book Company, Seattle',
        type: 'Author Talk',
        image:
          'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=800',
        description:
          'Bestselling author Elena Richards discusses the art of writing dark romance, her creative process, and reads from her latest novel.',
        attendees: 60,
        maxAttendees: 100,
        ticketPrice: 10,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
      {
        id: 202,
        title: 'Fantasy Worldbuilding with Amara Knight',
        date: 'Nov 8, 2026',
        time: '8:00 PM - 9:30 PM EST',
        location: 'Zoom',
        type: 'Author Talk',
        image:
          'https://images.unsplash.com/photo-1576872381149-7847515ce5d8?w=800',
        description:
          'Join Amara Knight for a deep dive into fantasy worldbuilding techniques, from magic systems to political intrigue.',
        attendees: 150,
        maxAttendees: 500,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
      {
        id: 203,
        title: 'Poetry & Prose Evening',
        date: 'Dec 3, 2026',
        time: '6:00 PM - 8:00 PM',
        location: 'Brookline Booksmith, Boston',
        type: 'Author Talk',
        image:
          'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800',
        description:
          'An intimate evening of readings featuring three acclaimed poets and novelists exploring the intersection of poetry and prose.',
        attendees: 35,
        maxAttendees: 50,
        ticketPrice: 12,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
    ],
  },
  'author-panels': {
    slug: 'author-panels',
    title: 'Author Panels & Q&A',
    subtitle: 'Panel discussions and Q&A sessions with your favorite authors',
    searchPlaceholder: 'Search author panels by location...',
    attendeeLabel: 'attending',
    emptyMessage: 'No author panels found.',
    sampleEvents: [
      {
        id: 301,
        title: 'Women in Fantasy Panel',
        date: 'Oct 28, 2026',
        time: '2:00 PM - 4:00 PM',
        location: "Powell's City of Books, Portland",
        type: 'Author Panel',
        image:
          'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800',
        description:
          'Four bestselling fantasy authors discuss representation, worldbuilding, and the future of women in fantasy literature.',
        attendees: 80,
        maxAttendees: 120,
        ticketPrice: 15,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
      {
        id: 302,
        title: 'Indie Publishing Roundtable',
        date: 'Nov 15, 2026',
        time: '7:00 PM - 8:30 PM EST',
        location: 'Zoom',
        type: 'Author Panel',
        image:
          'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
        description:
          'Five successful indie authors share their journeys, marketing strategies, and lessons learned in self-publishing.',
        attendees: 180,
        maxAttendees: 300,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
      {
        id: 303,
        title: 'Dark Romance Authors Q&A',
        date: 'Dec 10, 2026',
        time: '6:00 PM - 8:00 PM',
        location: 'Barnes & Noble Union Square, New York',
        type: 'Author Panel',
        image:
          'https://images.unsplash.com/photo-1513001900722-370f803f498d?w=800',
        description:
          'An uncensored Q&A with three of the biggest names in dark romance. Audience questions welcome!',
        attendees: 95,
        maxAttendees: 100,
        ticketPrice: 20,
        status: {
          label: 'Almost Full',
          classes: 'bg-amber-100 text-amber-800',
        },
        country: 'USA',
      },
    ],
  },
  'multi-author-signings': {
    slug: 'multi-author-signings',
    title: 'Multi-Author Signings',
    subtitle: 'Large signing events featuring multiple authors in one venue',
    searchPlaceholder: 'Search multi-author signings by location...',
    attendeeLabel: 'attending',
    emptyMessage: 'No multi-author signings found.',
    sampleEvents: [
      {
        id: 401,
        title: 'Romance Mega-Signing 2026',
        date: 'Nov 12, 2026',
        time: '10:00 AM - 5:00 PM',
        location: 'Hyatt Regency, Dallas',
        type: 'Multi-Author Signing',
        image:
          'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=800',
        description:
          'Over 50 romance authors under one roof! Get books signed, meet your favorites, and discover new voices.',
        attendees: 450,
        maxAttendees: 800,
        ticketPrice: 40,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
        link: '/events/convention-1',
      },
      {
        id: 402,
        title: 'Fantasy & Sci-Fi Author Fair',
        date: 'Dec 5, 2026',
        time: '11:00 AM - 4:00 PM',
        location: 'Convention Center, Denver',
        type: 'Multi-Author Signing',
        image:
          'https://images.unsplash.com/photo-1576872381149-7847515ce5d8?w=800',
        description:
          '30+ fantasy and sci-fi authors with signings, panels, and exclusive merchandise.',
        attendees: 280,
        maxAttendees: 500,
        ticketPrice: 25,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
      {
        id: 403,
        title: 'Indie Author Showcase',
        date: 'Jan 15, 2027',
        time: '12:00 PM - 6:00 PM',
        location: 'Marriott Marquis, Atlanta',
        type: 'Multi-Author Signing',
        image:
          'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800',
        description:
          'A celebration of indie publishing with 40 self-published authors across all genres.',
        attendees: 150,
        maxAttendees: 400,
        ticketPrice: 20,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
    ],
  },
  'local-authors': {
    slug: 'local-authors',
    title: 'Local Author Events',
    subtitle: 'Discover and support authors in your local community',
    searchPlaceholder: 'Search local author events by location...',
    attendeeLabel: 'attending',
    emptyMessage: 'No local author events found.',
    sampleEvents: [
      {
        id: 501,
        title: 'Meet Local Authors Night',
        date: 'Oct 22, 2026',
        time: '6:00 PM - 8:00 PM',
        location: 'Community Library, Austin',
        type: 'Local Author Event',
        image:
          'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800',
        description:
          'An evening with five local Austin authors reading from their latest works and signing books.',
        attendees: 25,
        maxAttendees: 50,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 502,
        title: 'Portland Writers Showcase',
        date: 'Nov 8, 2026',
        time: '2:00 PM - 5:00 PM',
        location: 'Alberta Street Books, Portland',
        type: 'Local Author Event',
        image:
          'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800',
        description:
          'Portland-based authors share their stories and sign copies. Featuring debut and established local voices.',
        attendees: 18,
        maxAttendees: 40,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 503,
        title: 'Chicago Indie Author Fair',
        date: 'Dec 12, 2026',
        time: '10:00 AM - 3:00 PM',
        location: 'Harold Washington Library, Chicago',
        type: 'Local Author Event',
        image:
          'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800',
        description:
          'Browse books from 20+ Chicago-area indie authors. Perfect for holiday gift shopping!',
        attendees: 40,
        maxAttendees: 100,
        price: 'Free',
        country: 'USA',
      },
    ],
  },
  'reading-meetups': {
    slug: 'reading-meetups',
    title: 'Reading Meetups',
    subtitle:
      'Casual reading sessions in parks, cafés, and other social settings',
    searchPlaceholder: 'Search meetups by location...',
    attendeeLabel: 'attending',
    emptyMessage: 'No reading meetups found.',
    sampleEvents: [
      {
        id: 601,
        title: 'Park Reading Afternoon',
        date: 'Oct 18, 2026',
        time: '2:00 PM - 5:00 PM',
        location: 'Zilker Park, Austin',
        type: 'Reading Meetup',
        image:
          'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800',
        description:
          'Bring a blanket, your current read, and enjoy an afternoon of reading in the park with fellow book lovers.',
        attendees: 15,
        maxAttendees: 30,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 602,
        title: 'Café Reading Circle',
        date: 'Nov 1, 2026',
        time: '10:00 AM - 12:00 PM',
        location: 'Blue Bottle Coffee, San Francisco',
        type: 'Reading Meetup',
        image:
          'https://images.unsplash.com/photo-1513001900722-370f803f498d?w=800',
        description:
          'A casual morning reading session at a cozy café. Coffee and pastries available for purchase.',
        attendees: 10,
        maxAttendees: 20,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 603,
        title: 'Beach Reads Meetup',
        date: 'Nov 15, 2026',
        time: '4:00 PM - 7:00 PM',
        location: 'South Beach, Miami',
        type: 'Reading Meetup',
        image:
          'https://images.unsplash.com/photo-1519682577862-22b62b24e493?w=800',
        description:
          'Read by the ocean! Bring your beach read and join us for a sunset reading session.',
        attendees: 20,
        maxAttendees: 40,
        price: 'Free',
        country: 'USA',
      },
    ],
  },
  'book-swaps': {
    slug: 'book-swaps',
    title: 'Book Swaps',
    subtitle:
      'Trade books with fellow readers and discover your next great read',
    searchPlaceholder: 'Search book swaps by location...',
    attendeeLabel: 'attending',
    emptyMessage: 'No book swaps found.',
    sampleEvents: [
      {
        id: 701,
        title: 'Community Book Swap',
        date: 'Oct 30, 2026',
        time: '11:00 AM - 3:00 PM',
        location: 'Central Park, New York',
        type: 'Book Swap',
        image:
          'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800',
        description:
          "Bring books you've finished and swap them for new-to-you reads. All genres welcome!",
        attendees: 35,
        maxAttendees: 60,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 702,
        title: 'Romance Book Exchange',
        date: 'Nov 14, 2026',
        time: '2:00 PM - 5:00 PM',
        location: 'Tattered Cover, Denver',
        type: 'Book Swap',
        image:
          'https://images.unsplash.com/photo-1513001900722-370f803f498d?w=800',
        description:
          'Romance-only book swap! Bring your favorite romance novels and find your next obsession.',
        attendees: 20,
        maxAttendees: 40,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 703,
        title: 'Holiday Book Swap Party',
        date: 'Dec 18, 2026',
        time: '6:00 PM - 9:00 PM',
        location: 'BookPeople, Austin',
        type: 'Book Swap',
        image:
          'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800',
        description:
          'Wrap a book, swap a book! A festive holiday book exchange with hot cocoa and treats.',
        attendees: 28,
        maxAttendees: 50,
        price: 'Free',
        country: 'USA',
      },
    ],
  },
  'book-clubs-in-person': {
    slug: 'book-clubs-in-person',
    title: 'In-Person Book Clubs',
    subtitle: 'Join local book clubs meeting in person to discuss great reads',
    searchPlaceholder: 'Search in-person book clubs by location...',
    attendeeLabel: 'members',
    emptyMessage: 'No in-person book clubs found.',
    sampleEvents: [
      {
        id: 801,
        title: 'Thriller Thursday Book Club',
        date: 'Oct 24, 2026',
        time: '7:00 PM - 9:00 PM',
        location: 'The Book Loft, Columbus',
        type: 'In-Person Book Club',
        image:
          'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800',
        description:
          'Monthly thriller book club. This month: "The Silent Patient" by Alex Michaelides.',
        attendees: 12,
        maxAttendees: 20,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 802,
        title: 'Romance Readers Circle',
        date: 'Nov 5, 2026',
        time: '6:30 PM - 8:30 PM',
        location: 'Raven Book Store, Lawrence',
        type: 'In-Person Book Club',
        image:
          'https://images.unsplash.com/photo-1513001900722-370f803f498d?w=800',
        description:
          'A welcoming romance book club for all sub-genres. Wine and snacks provided!',
        attendees: 15,
        maxAttendees: 25,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 803,
        title: 'Sci-Fi & Fantasy Book Club',
        date: 'Dec 1, 2026',
        time: '7:00 PM - 9:00 PM',
        location: 'Mysterious Galaxy, San Diego',
        type: 'In-Person Book Club',
        image:
          'https://images.unsplash.com/photo-1576872381149-7847515ce5d8?w=800',
        description:
          'Explore new worlds monthly. This month: "The Fifth Season" by N.K. Jemisin.',
        attendees: 18,
        maxAttendees: 30,
        price: 'Free',
        country: 'USA',
      },
    ],
  },
  'book-clubs-virtual': {
    slug: 'book-clubs-virtual',
    title: 'Virtual Book Clubs',
    subtitle: 'Join online book clubs from anywhere in the world',
    searchPlaceholder: 'Search virtual book clubs...',
    attendeeLabel: 'members',
    emptyMessage: 'No virtual book clubs found.',
    sampleEvents: [
      {
        id: 901,
        title: 'Global Romance Readers',
        date: 'Oct 28, 2026',
        time: '8:00 PM - 9:30 PM EST',
        location: 'Zoom',
        type: 'Virtual Book Club',
        image:
          'https://images.unsplash.com/photo-1513001900722-370f803f498d?w=800',
        description:
          'A worldwide romance book club meeting monthly on Zoom. All romance sub-genres welcome.',
        attendees: 45,
        maxAttendees: 100,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
      {
        id: 902,
        title: 'Fantasy Fireside Chat',
        date: 'Nov 12, 2026',
        time: '7:00 PM - 8:30 PM EST',
        location: 'Discord',
        type: 'Virtual Book Club',
        image:
          'https://images.unsplash.com/photo-1576872381149-7847515ce5d8?w=800',
        description:
          "Cozy up and discuss this month's fantasy pick in our Discord voice channel.",
        attendees: 30,
        maxAttendees: 50,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
      {
        id: 903,
        title: 'Diverse Voices Book Club',
        date: 'Dec 8, 2026',
        time: '6:00 PM - 7:30 PM EST',
        location: 'Zoom',
        type: 'Virtual Book Club',
        image:
          'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800',
        description:
          'Celebrating diverse authors and stories. Open to readers worldwide.',
        attendees: 55,
        maxAttendees: 80,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
    ],
  },
  'silent-book-clubs': {
    slug: 'silent-book-clubs',
    title: 'Silent Book Clubs',
    subtitle:
      "Quiet reading sessions in a social setting — bring whatever you're reading",
    searchPlaceholder: 'Search silent book clubs by location...',
    attendeeLabel: 'attending',
    emptyMessage: 'No silent book clubs found.',
    sampleEvents: [
      {
        id: 1001,
        title: 'Silent Reading at The Alchemist',
        date: 'Oct 20, 2026',
        time: '6:00 PM - 8:00 PM',
        location: 'The Alchemist Bar, San Francisco',
        type: 'Silent Book Club',
        image:
          'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800',
        description:
          'Read in peaceful company. First 30 minutes of socializing, then quiet reading time. Drinks available.',
        attendees: 18,
        maxAttendees: 30,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 1002,
        title: 'Library Silent Reading Hour',
        date: 'Nov 3, 2026',
        time: '5:00 PM - 7:00 PM',
        location: 'Brooklyn Public Library, New York',
        type: 'Silent Book Club',
        image:
          'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800',
        description:
          'A dedicated quiet reading session in the beautiful main reading room. All ages welcome.',
        attendees: 22,
        maxAttendees: 40,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 1003,
        title: 'Café Silence',
        date: 'Dec 7, 2026',
        time: '10:00 AM - 12:00 PM',
        location: 'Intelligentsia Coffee, Chicago',
        type: 'Silent Book Club',
        image:
          'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800',
        description:
          'Saturday morning silent reading at a cozy café. Bring your book and enjoy the quiet company.',
        attendees: 12,
        maxAttendees: 25,
        price: 'Free',
        country: 'USA',
      },
    ],
  },
  'conventions-festivals': {
    slug: 'conventions-festivals',
    title: 'Conventions & Festivals',
    subtitle:
      'Multi-day book conventions, literary festivals, and publishing expos',
    searchPlaceholder: 'Search conventions & festivals by location...',
    attendeeLabel: 'registered',
    emptyMessage: 'No conventions or festivals found.',
    sampleEvents: [
      {
        id: 1101,
        title: 'BookCon 2026',
        date: 'Nov 15, 2026',
        time: '9:00 AM - 6:00 PM',
        location: 'Javits Center, New York',
        type: 'Convention',
        image:
          'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=800',
        description:
          'The biggest book convention of the year with panels, signings, exclusive ARCs, and more.',
        attendees: 2500,
        maxAttendees: 5000,
        ticketPrice: 45,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
      {
        id: 1102,
        title: 'Edinburgh Book Festival',
        date: 'Aug 10, 2027',
        time: '10:00 AM - 9:00 PM',
        location: 'Charlotte Square, Edinburgh',
        type: 'Festival',
        image:
          'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800',
        description:
          "One of the world's premier literary festivals with hundreds of authors and events over two weeks.",
        attendees: 1800,
        maxAttendees: 3000,
        ticketPrice: 35,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'UK',
      },
      {
        id: 1103,
        title: 'Romance Writers Convention',
        date: 'Jul 20, 2027',
        time: '8:00 AM - 10:00 PM',
        location: 'Marriott Marquis, San Diego',
        type: 'Convention',
        image:
          'https://images.unsplash.com/photo-1513001900722-370f803f498d?w=800',
        description:
          'Four days of romance writing workshops, agent pitches, networking, and the legendary book signing.',
        attendees: 1200,
        maxAttendees: 2000,
        ticketPrice: 150,
        status: { label: 'Early Bird', classes: 'bg-blue-100 text-blue-800' },
        country: 'USA',
      },
    ],
  },
  'reading-retreats': {
    slug: 'reading-retreats',
    title: 'Reading Retreats',
    subtitle: 'Multi-day reading getaways in beautiful, relaxing locations',
    searchPlaceholder: 'Search reading retreats by location...',
    attendeeLabel: 'registered',
    emptyMessage: 'No reading retreats found.',
    sampleEvents: [
      {
        id: 1201,
        title: 'Mountain Reading Retreat',
        date: 'Nov 20, 2026',
        time: 'Fri 4 PM - Sun 12 PM',
        location: 'Asheville, North Carolina',
        type: 'Reading Retreat',
        image:
          'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800',
        description:
          'A weekend of reading in the Blue Ridge Mountains. Includes lodging, meals, and curated book selections.',
        attendees: 15,
        maxAttendees: 20,
        ticketPrice: 350,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
      {
        id: 1202,
        title: 'Coastal Book Getaway',
        date: 'Jan 10, 2027',
        time: 'Thu 2 PM - Sun 11 AM',
        location: 'Cannon Beach, Oregon',
        type: 'Reading Retreat',
        image:
          'https://images.unsplash.com/photo-1519682577862-22b62b24e493?w=800',
        description:
          'Four days of ocean views, cozy reading nooks, and evening book discussions by the fire.',
        attendees: 10,
        maxAttendees: 16,
        ticketPrice: 450,
        status: {
          label: 'Almost Full',
          classes: 'bg-amber-100 text-amber-800',
        },
        country: 'USA',
      },
      {
        id: 1203,
        title: 'Lake House Reading Weekend',
        date: 'Feb 14, 2027',
        time: 'Sat 10 AM - Sun 4 PM',
        location: 'Lake Geneva, Wisconsin',
        type: 'Reading Retreat',
        image:
          'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800',
        description:
          "A Valentine's weekend for book lovers. Read, relax, and connect with fellow readers.",
        attendees: 8,
        maxAttendees: 12,
        ticketPrice: 200,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
    ],
  },
  'midnight-releases': {
    slug: 'midnight-releases',
    title: 'Midnight Release Events',
    subtitle:
      'Be the first to get your hands on the most anticipated new releases',
    searchPlaceholder: 'Search midnight releases by location...',
    attendeeLabel: 'attending',
    emptyMessage: 'No midnight release events found.',
    sampleEvents: [
      {
        id: 1301,
        title: 'Midnight Release: Crown of Thorns',
        date: 'Nov 30, 2026',
        time: '11:00 PM - 1:00 AM',
        location: 'Barnes & Noble, Times Square, New York',
        type: 'Midnight Release',
        image:
          'https://images.unsplash.com/photo-1576872381149-7847515ce5d8?w=800',
        description:
          'Be the first to grab the final book in the bestselling Crown series! Costumes encouraged.',
        attendees: 120,
        maxAttendees: 200,
        price: 'Free',
        country: 'USA',
      },
      {
        id: 1302,
        title: 'Midnight Magic: Fantasy Release Party',
        date: 'Dec 15, 2026',
        time: '10:00 PM - 12:30 AM',
        location: 'Waterstones Piccadilly, London',
        type: 'Midnight Release',
        image:
          'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800',
        description:
          "Celebrate the release of the year's most anticipated fantasy novel with themed activities and midnight signing.",
        attendees: 85,
        maxAttendees: 150,
        price: 'Free',
        country: 'UK',
      },
      {
        id: 1303,
        title: 'Romance at Midnight',
        date: 'Jan 5, 2027',
        time: '11:00 PM - 1:00 AM',
        location: 'The Ripped Bodice, Los Angeles',
        type: 'Midnight Release',
        image:
          'https://images.unsplash.com/photo-1513001900722-370f803f498d?w=800',
        description:
          'Ring in the new year with the hottest romance release. Champagne toast at midnight!',
        attendees: 45,
        maxAttendees: 60,
        price: 'Free',
        country: 'USA',
      },
    ],
  },
  'writing-workshops': {
    slug: 'writing-workshops',
    title: 'Writing Workshops',
    subtitle: 'Hands-on workshops to improve your writing craft',
    searchPlaceholder: 'Search workshops by location...',
    attendeeLabel: 'enrolled',
    emptyMessage: 'No writing workshops found.',
    sampleEvents: [
      {
        id: 1401,
        title: 'Plotting Your Novel',
        date: 'Oct 25, 2026',
        time: '10:00 AM - 4:00 PM',
        location: 'Literary Arts Center, Portland',
        type: 'Writing Workshop',
        image:
          'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800',
        description:
          'A full-day workshop on novel plotting techniques. Learn three-act structure, beat sheets, and outlining methods.',
        attendees: 18,
        maxAttendees: 25,
        ticketPrice: 85,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
      {
        id: 1402,
        title: 'Character Development Masterclass',
        date: 'Nov 10, 2026',
        time: '7:00 PM - 9:00 PM EST',
        location: 'Zoom',
        type: 'Writing Workshop',
        image:
          'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
        description:
          'Learn to create compelling, multi-dimensional characters. Interactive exercises included.',
        attendees: 40,
        maxAttendees: 60,
        ticketPrice: 35,
        isVirtual: true,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'Global',
      },
      {
        id: 1403,
        title: 'Dialogue Workshop',
        date: 'Dec 8, 2026',
        time: '1:00 PM - 5:00 PM',
        location: 'StoryStudio Chicago, Chicago',
        type: 'Writing Workshop',
        image:
          'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800',
        description:
          'Master the art of writing dialogue that sounds natural, reveals character, and advances your plot.',
        attendees: 12,
        maxAttendees: 20,
        ticketPrice: 65,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
    ],
  },
  'publishing-workshops': {
    slug: 'publishing-workshops',
    title: 'Publishing Workshops',
    subtitle:
      'Learn the ins and outs of publishing, marketing, and book business',
    searchPlaceholder: 'Search publishing workshops by location...',
    attendeeLabel: 'enrolled',
    emptyMessage: 'No publishing workshops found.',
    sampleEvents: [
      {
        id: 1501,
        title: 'Self-Publishing 101',
        date: 'Oct 30, 2026',
        time: '10:00 AM - 3:00 PM',
        location: 'WeWork, Los Angeles',
        type: 'Publishing Workshop',
        image:
          'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
        description:
          'Everything you need to know to self-publish your first book. Covers formatting, covers, distribution, and marketing.',
        attendees: 22,
        maxAttendees: 30,
        ticketPrice: 75,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
      {
        id: 1502,
        title: 'Book Marketing Bootcamp',
        date: 'Nov 18, 2026',
        time: '6:00 PM - 9:00 PM EST',
        location: 'Zoom',
        type: 'Publishing Workshop',
        image:
          'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800',
        description:
          'Intensive marketing workshop covering social media, newsletters, ads, and launch strategies.',
        attendees: 55,
        maxAttendees: 80,
        ticketPrice: 45,
        isVirtual: true,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'Global',
      },
      {
        id: 1503,
        title: 'Query Letter Workshop',
        date: 'Dec 5, 2026',
        time: '2:00 PM - 5:00 PM',
        location: 'Grub Street, Boston',
        type: 'Publishing Workshop',
        image:
          'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800',
        description:
          'Craft a compelling query letter with guidance from a literary agent. Includes personalized feedback.',
        attendees: 10,
        maxAttendees: 15,
        ticketPrice: 60,
        status: {
          label: 'Almost Full',
          classes: 'bg-amber-100 text-amber-800',
        },
        country: 'USA',
      },
    ],
  },
  'craft-masterclasses': {
    slug: 'craft-masterclasses',
    title: 'Craft Masterclasses',
    subtitle: 'Advanced masterclasses on specific writing craft topics',
    searchPlaceholder: 'Search masterclasses by location...',
    attendeeLabel: 'enrolled',
    emptyMessage: 'No craft masterclasses found.',
    sampleEvents: [
      {
        id: 1601,
        title: 'Tension & Pacing Masterclass',
        date: 'Nov 5, 2026',
        time: '10:00 AM - 4:00 PM',
        location: 'Hugo House, Seattle',
        type: 'Craft Masterclass',
        image:
          'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800',
        description:
          'A deep dive into creating unputdownable tension and perfect pacing, taught by a NYT bestselling thriller author.',
        attendees: 15,
        maxAttendees: 20,
        ticketPrice: 120,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
      {
        id: 1602,
        title: 'Voice & Style Intensive',
        date: 'Dec 12, 2026',
        time: '7:00 PM - 9:30 PM EST',
        location: 'Zoom',
        type: 'Craft Masterclass',
        image:
          'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
        description:
          'Find and refine your unique writing voice. Exercises, examples, and personalized feedback.',
        attendees: 25,
        maxAttendees: 35,
        ticketPrice: 55,
        isVirtual: true,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'Global',
      },
      {
        id: 1603,
        title: 'Worldbuilding for Fantasy Writers',
        date: 'Jan 20, 2027',
        time: '9:00 AM - 5:00 PM',
        location: 'The Writers Room, New York',
        type: 'Craft Masterclass',
        image:
          'https://images.unsplash.com/photo-1576872381149-7847515ce5d8?w=800',
        description:
          'Full-day intensive on building immersive fantasy worlds. Magic systems, cultures, geography, and history.',
        attendees: 12,
        maxAttendees: 18,
        ticketPrice: 150,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'USA',
      },
    ],
  },
  'virtual-author-events': {
    slug: 'virtual-author-events',
    title: 'Virtual Author Events',
    subtitle: 'Online author Q&As, readings, and meet-and-greets from anywhere',
    searchPlaceholder: 'Search virtual author events...',
    attendeeLabel: 'attending',
    emptyMessage: 'No virtual author events found.',
    sampleEvents: [
      {
        id: 1701,
        title: 'Live Q&A with Elena Richards',
        date: 'Oct 22, 2026',
        time: '8:00 PM - 9:30 PM EST',
        location: 'Instagram Live',
        type: 'Virtual Author Event',
        image:
          'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=800',
        description:
          'Ask Elena anything about her writing process, upcoming releases, and life as a full-time author.',
        attendees: 300,
        maxAttendees: 1000,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
      {
        id: 1702,
        title: 'Virtual Reading: New Fantasy Release',
        date: 'Nov 15, 2026',
        time: '7:00 PM - 8:00 PM EST',
        location: 'YouTube Live',
        type: 'Virtual Author Event',
        image:
          'https://images.unsplash.com/photo-1576872381149-7847515ce5d8?w=800',
        description:
          'Exclusive first reading from the upcoming fantasy novel, followed by audience Q&A.',
        attendees: 500,
        maxAttendees: 2000,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
      {
        id: 1703,
        title: 'Author Fireside Chat',
        date: 'Dec 5, 2026',
        time: '6:00 PM - 7:30 PM EST',
        location: 'Zoom',
        type: 'Virtual Author Event',
        image:
          'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
        description:
          'An intimate virtual conversation with three debut authors about their journeys to publication.',
        attendees: 80,
        maxAttendees: 150,
        isVirtual: true,
        ticketPrice: 5,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'Global',
      },
    ],
  },
  'livestream-discussions': {
    slug: 'livestream-discussions',
    title: 'Livestream Book Discussions',
    subtitle: 'Live-streamed book discussions, reviews, and reading sessions',
    searchPlaceholder: 'Search livestream discussions...',
    attendeeLabel: 'watching',
    emptyMessage: 'No livestream discussions found.',
    sampleEvents: [
      {
        id: 1801,
        title: 'BookTok Live: October Reads',
        date: 'Oct 28, 2026',
        time: '8:00 PM - 9:30 PM EST',
        location: 'TikTok Live',
        type: 'Livestream Discussion',
        image:
          'https://images.unsplash.com/photo-1513001900722-370f803f498d?w=800',
        description:
          'Join popular BookTok creators as they discuss their favorite October reads and take audience recommendations.',
        attendees: 800,
        maxAttendees: 5000,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
      {
        id: 1802,
        title: 'Spicy Romance Roundup',
        date: 'Nov 20, 2026',
        time: '9:00 PM - 10:30 PM EST',
        location: 'YouTube Live',
        type: 'Livestream Discussion',
        image:
          'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800',
        description:
          'A lively livestream reviewing the spiciest romance releases of the month. Viewer polls and giveaways!',
        attendees: 450,
        maxAttendees: 3000,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
      {
        id: 1803,
        title: 'Fantasy Deep Dive',
        date: 'Dec 15, 2026',
        time: '7:00 PM - 8:30 PM EST',
        location: 'Twitch',
        type: 'Livestream Discussion',
        image:
          'https://images.unsplash.com/photo-1576872381149-7847515ce5d8?w=800',
        description:
          "An in-depth discussion of the year's best fantasy novels with special author guests.",
        attendees: 200,
        maxAttendees: 1000,
        isVirtual: true,
        price: 'Free',
        country: 'Global',
      },
    ],
  },
  'online-workshops': {
    slug: 'online-workshops',
    title: 'Online Workshops',
    subtitle:
      'Virtual workshops on writing, publishing, and craft from anywhere',
    searchPlaceholder: 'Search online workshops...',
    attendeeLabel: 'enrolled',
    emptyMessage: 'No online workshops found.',
    sampleEvents: [
      {
        id: 1901,
        title: 'Writing Romance That Sells',
        date: 'Oct 30, 2026',
        time: '7:00 PM - 9:00 PM EST',
        location: 'Zoom',
        type: 'Online Workshop',
        image:
          'https://images.unsplash.com/photo-1513001900722-370f803f498d?w=800',
        description:
          'Learn market trends, tropes readers love, and how to write romance that connects with audiences.',
        attendees: 35,
        maxAttendees: 50,
        ticketPrice: 30,
        isVirtual: true,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'Global',
      },
      {
        id: 1902,
        title: 'Cover Design for Authors',
        date: 'Nov 22, 2026',
        time: '2:00 PM - 4:00 PM EST',
        location: 'Zoom',
        type: 'Online Workshop',
        image:
          'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800',
        description:
          'Learn to brief designers, understand genre conventions, and evaluate cover mockups like a pro.',
        attendees: 28,
        maxAttendees: 40,
        ticketPrice: 25,
        isVirtual: true,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'Global',
      },
      {
        id: 1903,
        title: 'Newsletter Growth Strategies',
        date: 'Dec 10, 2026',
        time: '6:00 PM - 8:00 PM EST',
        location: 'Zoom',
        type: 'Online Workshop',
        image:
          'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800',
        description:
          'Build and grow your author newsletter. Covers lead magnets, automation, and engagement strategies.',
        attendees: 42,
        maxAttendees: 60,
        ticketPrice: 20,
        isVirtual: true,
        status: {
          label: 'Tickets Live',
          classes: 'bg-green-100 text-green-800',
        },
        country: 'Global',
      },
    ],
  },
}
